import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '@/services/api'
import { getBestTechnology, getSignalQuality, NetworkCheckResult } from '@/services/networkCoverage'
import { useAuthStore } from '@/stores/authStore'

/**
 * After a coverage check (Sales Strategy, owner 2026-10-15): 415 people checked coverage in
 * 60 days and only 17 ordered — the result page had no next step. Shows the plans buttons, and
 * — only when the owner has approved and switched on the "coverage result" message — a
 * "send me my result" box so we can follow up.
 */
const NAMES: Record<string, string> = { three: 'Three', o2: 'O2', vodafone: 'Vodafone', ee: 'EE' }

export function coverageSummary(result: NetworkCheckResult): string {
  return Object.entries(result.coverage || {})
    .filter(([, c]: any) => !c?.error)
    .map(([n, c]: any) => `${NAMES[n] || n}: ${getBestTechnology(c)}, ${getSignalQuality(c)}`)
    .join(' · ')
}

export default function CoverageNextSteps({ result, isPortal }: { result: NetworkCheckResult; isPortal: boolean }) {
  const user = useAuthStore((s) => s.user) as any
  const [capture, setCapture] = useState(false)
  const [f, setF] = useState({ name: '', email: '', mobile: '', consent: false })
  const [state, setState] = useState<{ ok?: boolean; text?: string; busy?: boolean }>({})
  useEffect(() => {
    if (isPortal) return
    api.get('/sales/config').then((r) => setCapture(!!r.data.coverage_capture)).catch(() => {})
  }, [isPortal])
  useEffect(() => {
    if (user) setF((x) => ({ ...x, name: x.name || user.first_name || '', email: x.email || user.email || '', mobile: x.mobile || user.mobile_number || '' }))
  }, [user])
  const summary = coverageSummary(result)
  const send = async () => {
    setState({ busy: true })
    try {
      await api.post('/sales/coverage-lead', { ...f, postcode: result.postcode, summary })
      setState({ ok: true, text: 'Sent — check your inbox / messages.' })
    } catch (e: any) { setState({ ok: false, text: e?.response?.data?.detail || 'Sorry, that did not work.' }) }
  }
  if (!summary) return null
  return (
    <div className="max-w-3xl mx-auto bg-white rounded-xl shadow p-6 text-center">
      <h3 className="text-xl font-bold mb-1">Ready to get connected at {result.postcode}?</h3>
      <p className="text-gray-600 mb-4">Order online in a few minutes, with free Tracked 48 delivery.</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/broadband" className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700">See broadband plans</Link>
        <Link to="/sim-only" className="px-6 py-3 border-2 border-primary-600 text-primary-700 rounded-lg font-semibold hover:bg-primary-50">See SIM plans</Link>
      </div>
      {capture && (
        <div className="mt-6 pt-6 border-t text-left">
          <p className="font-semibold mb-2">Not ready yet? We'll send you this result.</p>
          {state.ok ? <p className="text-green-700">{state.text}</p> : (
            <div className="grid sm:grid-cols-3 gap-2">
              <input className="border rounded-lg px-3 py-2" placeholder="First name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
              <input className="border rounded-lg px-3 py-2" placeholder="Mobile (optional)" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} />
              <label className="sm:col-span-3 text-xs text-gray-600 flex items-start gap-2">
                <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5" />
                Send me this result and a follow-up about Silux Mobile plans. I can opt out at any time.
              </label>
              <button disabled={state.busy || !f.name || !(f.email || f.mobile) || !f.consent} onClick={send}
                className="sm:col-span-3 px-4 py-2 bg-gray-900 text-white rounded-lg disabled:opacity-40">Send me my result</button>
              {state.ok === false && <p className="sm:col-span-3 text-sm text-red-600">{state.text}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
